// Pure track-language rules, shared by the player adapter. Free of Shaka and
// React so the selection can be unit-tested by a direct call.

export interface LanguageTrack {
  language: string;
}

/** BCP-47 base language: "pt-BR" → "pt", "FR" → "fr". */
const baseLanguage = (tag: string) => tag.toLowerCase().split("-")[0];

/**
 * Whether a tag actually names a language. Streams report `""` (native HLS
 * exposes no language) or the BCP-47 `"und"` (a rendition declaring no
 * LANGUAGE) — neither is worth matching a locale against, nor persisting as a
 * user preference.
 */
export const isKnownLanguage = (
  tag: string | null | undefined,
): tag is string => !!tag && baseLanguage(tag) !== "und";

/**
 * Exact match first ("pt-BR"), then base language ("fr-CA" ↔ "fr") so a
 * manifest shipping plain "fr" still serves a "fr-CA" viewer. `null` when the
 * manifest has nothing for that language — the caller keeps the stream default.
 */
export const findTrackForLocale = <T extends LanguageTrack>(
  tracks: ReadonlyArray<T>,
  locale: string,
): T | null => {
  if (!locale) return null;
  const wanted = locale.toLowerCase();
  return (
    tracks.find((track) => track.language.toLowerCase() === wanted) ??
    tracks.find(
      (track) => baseLanguage(track.language) === baseLanguage(wanted),
    ) ??
    null
  );
};

interface DefaultTextTrackInput<TText extends LanguageTrack> {
  textTracks: ReadonlyArray<TText>;
  /** Language of the audio that will actually play. */
  audioLanguage: string | undefined;
  locale: string;
}

/**
 * The language every show is shot in. Only the dubbed ones ship audio
 * renditions; the rest mux a single English track into the variants, which
 * exposes no audio language at all (or the BCP-47 "und" when a rendition omits
 * LANGUAGE). Such a stream is therefore English, not "unknown" — the two other
 * readings both break: "unknown = foreign" turns English subtitles on for
 * English viewers across the whole undubbed catalog, and "unknown = leave it
 * alone" drops the subtitle fallback for everyone who isn't English on that
 * same catalog. Neither the manifest nor the API declares it, so it is a
 * constant here; replace it with the real value the day `Episode` exposes one.
 */
const SOURCE_AUDIO_LANGUAGE = "en";

/**
 * The subtitles to turn on when the user never picked any: they are a
 * **fallback**, stepping in only when the audio isn't in the app locale (region
 * aside — "fr-CA" audio satisfies a "fr" UI). Never forced on top of audio the
 * viewer already understands.
 */
export const resolveDefaultTextTrack = <TText extends LanguageTrack>({
  textTracks,
  audioLanguage,
  locale,
}: DefaultTextTrackInput<TText>): TText | null => {
  const spokenLanguage = isKnownLanguage(audioLanguage)
    ? audioLanguage
    : SOURCE_AUDIO_LANGUAGE;
  return baseLanguage(spokenLanguage) === baseLanguage(locale)
    ? null
    : findTrackForLocale(textTracks, locale);
};

export interface SelectableTrack extends LanguageTrack {
  active: boolean;
}

interface TrackSelectionInput<
  TAudio extends SelectableTrack,
  TText extends SelectableTrack,
> {
  audioTracks: ReadonlyArray<TAudio>;
  textTracks: ReadonlyArray<TText>;
  /** The app locale, `i18n.resolvedLanguage`. */
  locale: string;
  /** BCP-47 code of the audio the user picked; null → follow the locale. */
  audioPreference: string | null;
  /** BCP-47 code of the subtitles the user picked, "off", or null → fallback rule. */
  subtitlesPreference: string | "off" | null;
}

/**
 * What the audio should be in: the user's explicit pick, else the app locale.
 * One expression for both the pre-load `preferredAudio` hint and the post-load
 * selection — they must agree, or the hint fetches a rendition the selection
 * then switches away from, wasting a playlist and its segments per episode.
 */
export const preferredAudioLanguage = (
  audioPreference: string | null,
  locale: string,
): string => audioPreference ?? locale;

/**
 * The tracks a stream should play, from the stored preferences and the app
 * locale. Preferences hold a BCP-47 code, so a stored "fr" still matches a
 * "fr-CA" rendition; with no stored choice the locale decides the audio, and
 * subtitles follow the fallback rule above. `audio: null` keeps the stream
 * default, `text: null` means no subtitles. The player adapter (`useShaka`)
 * only applies what this returns, skipping a track already active.
 */
export const resolveTrackSelection = <
  TAudio extends SelectableTrack,
  TText extends SelectableTrack,
>({
  audioTracks,
  textTracks,
  locale,
  audioPreference,
  subtitlesPreference,
}: TrackSelectionInput<TAudio, TText>): {
  audio: TAudio | null;
  text: TText | null;
} => {
  const audio = findTrackForLocale(
    audioTracks,
    preferredAudioLanguage(audioPreference, locale),
  );
  const text =
    subtitlesPreference === "off"
      ? null
      : subtitlesPreference
        ? findTrackForLocale(textTracks, subtitlesPreference)
        : resolveDefaultTextTrack({
            textTracks,
            audioLanguage: (audio ?? audioTracks.find((track) => track.active))
              ?.language,
            locale,
          });
  return { audio, text };
};

const displayNames = new Map<string, Intl.DisplayNames>();

const displayNamesFor = (displayLocale: string) => {
  const cached = displayNames.get(displayLocale);
  if (cached) return cached;
  const created = new Intl.DisplayNames([displayLocale], { type: "language" });
  displayNames.set(displayLocale, created);
  return created;
};

export interface NamedTrack extends LanguageTrack {
  label?: string | null;
}

/**
 * Menu label for a track, in the app's own language. Streams without language
 * metadata report the BCP-47 "und" (undetermined) code — never show that raw,
 * hence the caller-supplied `undeterminedLabel` (translated by the call site,
 * which owns the i18n instance).
 */
export const trackName = (
  { label, language }: NamedTrack,
  displayLocale: string,
  undeterminedLabel: string,
): string => {
  if (label && label.length > 0) return label;
  if (!language || language === "und") return undeterminedLabel;
  try {
    return displayNamesFor(displayLocale).of(language) ?? language;
  } catch {
    return language;
  }
};
